/**
 * MyCraftzInstallOverlay — 6-step idea-installation funnel.
 *
 * Flow: Recall → Choose → Commit → Plan → Action → Reflect
 *
 * Step 1 (Recall):  pure local data (book.summary/insights/coreTheme).
 * Step 2 (Choose):  POST /api/mycraftz-install/ideas    (cached server-side)
 * Step 3 (Commit):  POST /api/mycraftz-install/why      (cached server-side)
 * Step 4 (Plan):    POST /api/mycraftz-install/plan     (cached server-side)
 *                   POST /api/mycraftz-install/quote    (cached server-side)
 * Step 5 (Action):  shows today's daily action; CTA hands off to /mycraftz/journey/atomic-habits
 *                   if it's one of the 5 showcase books, OR continues into Step 6 for the rest.
 * Step 6 (Reflect): POST /api/mycraftz-install/reflection (DB if logged-in, else email)
 *
 * Visual: reuses the existing parchment modal pattern. No new design language.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import { X, ArrowRight, ArrowLeft, Mic, MicOff, Check, Share2 } from "lucide-react";
import axios from "axios";
import { useLanguage } from "../lib/useLanguage";
import { withTransientRetry } from "../lib/apiRetry";
import "../styles/MyCraftzInstallOverlay.css";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/mycraftz-install`;

const STEPS = ["recall", "choose", "commit", "plan", "action", "reflect"];

// 5 showcase slugs that already have the dedicated 30-day engine
const SHOWCASE_SLUG_BY_TITLE = {
  "Atomic Habits": "atomic-habits",
  "Deep Work": "deep-work",
  "The Psychology of Money": "psychology-of-money",
  "Think and Grow Rich": "think-and-grow-rich",
  "Man's Search for Meaning": "mans-search-for-meaning",
};

function clampSentences(text, n) {
  if (!text) return "";
  const parts = text.match(/[^.!?।]+[.!?।]+/g);
  if (!parts || parts.length === 0) return text.slice(0, 320);
  return parts.slice(0, n).join(" ").trim();
}

function buildBookSnapshot(book) {
  // The frontend stores books in `bookExamples` with arbitrary fields.
  // Normalise to the backend's expected schema (BookSnapshot).
  const insights = Array.isArray(book.insights) ? book.insights.map((i) =>
    typeof i === "string" ? i : (i.summary || i.name || "")
  ) : [];
  const actionItems = Array.isArray(book.actionItems) ? book.actionItems : [];
  return {
    id: String(book.id),
    title: book.title || "",
    author: book.author || "",
    summary: book.summary || book.about || "",
    insights,
    actionItems,
    coreTheme: book.coreTheme || book.core_theme || "",
  };
}

export default function MyCraftzInstallOverlay({ open, book, t, onClose }) {
  const navigate = useNavigate();
  const [lang] = useLanguage();
  const [stepIdx, setStepIdx] = useState(0);
  const [ideas, setIdeas] = useState([]);
  const [loadingIdeas, setLoadingIdeas] = useState(false);
  const [ideasError, setIdeasError] = useState(null);
  const [selectedIdeaId, setSelectedIdeaId] = useState(null);
  const [why, setWhy] = useState("");
  const [loadingWhy, setLoadingWhy] = useState(false);
  const [committed, setCommitted] = useState(false);
  const [plan, setPlan] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [quote, setQuote] = useState(null);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [actionDone, setActionDone] = useState(false);
  const [reflection, setReflection] = useState({ noticed: "", difficult: "", tomorrow: "" });
  const [reflectionSaved, setReflectionSaved] = useState(false);

  // Reset on every fresh open
  useEffect(() => {
    if (open) {
      setStepIdx(0);
      setIdeas([]); setIdeasError(null); setSelectedIdeaId(null);
      setWhy(""); setCommitted(false);
      setPlan(null); setQuote(null);
      setActionDone(false);
      setReflection({ noticed: "", difficult: "", tomorrow: "" });
      setReflectionSaved(false);
    }
  }, [open, book]);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const copy = useMemo(() => (t && t.mycraftzInstall) || {}, [t]);
  const stepLabels = copy.steps || { recall: "Recall", choose: "Choose", commit: "Commit", plan: "Plan", action: "Action", reflect: "Reflect" };
  const snapshot = useMemo(() => (book ? buildBookSnapshot(book) : null), [book]);
  const selectedIdea = ideas.find((i) => i.id === selectedIdeaId) || null;

  const showcaseSlug = book ? SHOWCASE_SLUG_BY_TITLE[book.title] : null;

  // ─────── API calls ───────
  const fetchIdeas = async () => {
    if (!snapshot) return;
    setLoadingIdeas(true); setIdeasError(null);
    try {
      const res = await axios.post(`${API}/ideas`, { book: snapshot });
      const englishIdeas = res.data?.ideas || [];
      // Translate on the fly for non-English locales so the overlay's
      // Choose/Commit/Plan steps read in the user's language. English
      // is a no-op — we just render the /ideas response as-is.
      if (lang !== "en" && Array.isArray(englishIdeas) && englishIdeas.length > 0) {
        try {
          const tRes = await withTransientRetry(() =>
            axios.post(`${API}/ideas-translate`, {
              book_id: snapshot.id,
              book_title: snapshot.title,
              targetLanguage: lang,
              ideas: englishIdeas.map((i) => ({
                id: i.id, title: i.title || "", description: i.description || "",
              })),
            })
          );
          const translated = tRes.data?.ideas;
          if (tRes.data?.lang === lang && Array.isArray(translated)) {
            // Merge translated title/description onto the English rows so
            // ids and downstream flows stay stable.
            const byId = new Map(translated.map((t2) => [Number(t2.id), t2]));
            setIdeas(englishIdeas.map((eng) => {
              const tr = byId.get(Number(eng.id));
              return tr
                ? { ...eng, title: tr.title || eng.title, description: tr.description || eng.description }
                : eng;
            }));
            return;
          }
        } catch (tErr) {
        }
      }
      setIdeas(englishIdeas);
    } catch (err) {
      setIdeasError((copy.errors && copy.errors.generic) || "Something didn't load. Please try again.");
    } finally {
      setLoadingIdeas(false);
    }
  };

  const fetchWhy = async () => {
    if (!snapshot || !selectedIdea) return;
    setLoadingWhy(true);
    try {
      const res = await axios.post(`${API}/why`, {
        book: snapshot,
        idea_id: selectedIdea.id,
        idea_title: selectedIdea.title,
        targetLanguage: lang,
      });
      setWhy(res.data?.why || "");
    } catch (err) {
    } finally {
      setLoadingWhy(false);
    }
  };

  const fetchPlanAndQuote = async () => {
    if (!snapshot || !selectedIdea) return;
    setLoadingPlan(true); setLoadingQuote(true);
    try {
      const [pRes, qRes] = await Promise.all([
        axios.post(`${API}/plan`, {
          book: snapshot,
          idea_id: selectedIdea.id,
          idea_title: selectedIdea.title,
          targetLanguage: lang,
        }),
        // /quote returns Sanskrit + English translation — language-neutral
        // by design, so we don't pass targetLanguage.
        axios.post(`${API}/quote`, { book: snapshot, idea_id: selectedIdea.id, idea_title: selectedIdea.title }),
      ]);
      setPlan(pRes.data?.plan || null);
      setQuote(qRes.data?.quote || null);
    } catch (err) {
    } finally {
      setLoadingPlan(false); setLoadingQuote(false);
    }
  };

  const saveReflection = async () => {
    if (!snapshot || !selectedIdea) return;
    try {
      await axios.post(`${API}/reflection`, {
        book_title: snapshot.title,
        book_id: snapshot.id,
        idea_id: selectedIdea.id,
        idea_title: selectedIdea.title,
        noticed: reflection.noticed,
        difficult: reflection.difficult,
        tomorrow: reflection.tomorrow,
      });
      setReflectionSaved(true);
    } catch (err) {
      setReflectionSaved(true); // Still confirm; backend already absorbs failure gracefully
    }
  };

  // Trigger API calls on step entry
  useEffect(() => {
    if (!open || !snapshot) return;
    if (STEPS[stepIdx] === "choose" && ideas.length === 0 && !loadingIdeas && !ideasError) {
      fetchIdeas();
    }
    if (STEPS[stepIdx] === "commit" && selectedIdea && !why && !loadingWhy) {
      fetchWhy();
    }
    if (STEPS[stepIdx] === "plan" && selectedIdea && !plan && !loadingPlan) {
      fetchPlanAndQuote();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, stepIdx, snapshot, selectedIdea]);

  const next = () => setStepIdx((i) => Math.min(STEPS.length - 1, i + 1));
  const prev = () => setStepIdx((i) => Math.max(0, i - 1));

  const sharePlan = async () => {
    const text = (copy.reflect?.shareText || "I just installed an idea from {book} into my life with iCraftz™.")
      .replace("{book}", snapshot?.title || "a great book");
    if (navigator.share) {
      try { await navigator.share({ title: "iCraftz™", text, url: window.location.origin }); }
      catch { /* ignored */ }
    } else {
      const url = `https://wa.me/?text=${encodeURIComponent(text + " " + window.location.origin)}`;
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  if (!open || !book) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="mci-overlay"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        onClick={onClose}
        data-testid="mycraftz-install-overlay"
      >
        <motion.div
          className="mci-panel"
          role="dialog" aria-modal="true" aria-label="MyCraftz™ Install"
          initial={{ opacity: 0, y: 28, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 14, scale: 0.98 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
        >
          <button type="button" className="mci-close" onClick={onClose} aria-label="Close" data-testid="mci-close">
            <X size={18} />
          </button>

          {/* Step progress */}
          <ol className="mci-progress" aria-label="Step progress">
            {STEPS.map((key, i) => (
              <li
                key={key}
                className={`mci-progress-step ${i === stepIdx ? "is-active" : ""} ${i < stepIdx ? "is-done" : ""}`}
                data-testid={`mci-progress-${key}`}
              >
                <span className="mci-progress-dot" aria-hidden="true" />
                <span className="mci-progress-label">{stepLabels[key]}</span>
              </li>
            ))}
          </ol>

          <div className="mci-body">
            <AnimatePresence mode="wait">
              {/* STEP 1 — RECALL */}
              {STEPS[stepIdx] === "recall" && (
                <RecallStep key="recall" book={book} snapshot={snapshot} copy={copy.recall} onNext={next} />
              )}
              {/* STEP 2 — CHOOSE */}
              {STEPS[stepIdx] === "choose" && (
                <ChooseStep
                  key="choose" copy={copy.choose}
                  ideas={ideas} loading={loadingIdeas} error={ideasError}
                  onRetry={fetchIdeas}
                  selectedIdeaId={selectedIdeaId} onSelect={setSelectedIdeaId}
                  onNext={next} onBack={prev}
                />
              )}
              {/* STEP 3 — COMMIT */}
              {STEPS[stepIdx] === "commit" && (
                <CommitStep
                  key="commit" copy={copy.commit}
                  selectedIdea={selectedIdea} why={why} loading={loadingWhy}
                  committed={committed} onCommitChange={setCommitted}
                  onNext={next} onBack={prev}
                />
              )}
              {/* STEP 4 — PLAN */}
              {STEPS[stepIdx] === "plan" && (
                <PlanStep
                  key="plan" copy={copy.plan}
                  selectedIdea={selectedIdea}
                  plan={plan} loadingPlan={loadingPlan}
                  quote={quote} loadingQuote={loadingQuote}
                  onNext={next} onBack={prev}
                />
              )}
              {/* STEP 5 — ACTION */}
              {STEPS[stepIdx] === "action" && (
                <ActionStep
                  key="action" copy={copy.action}
                  plan={plan}
                  done={actionDone}
                  onDone={() => setActionDone(true)}
                  showContinueCta={Boolean(showcaseSlug)}
                  onContinueToEngine={() => {
                    onClose();
                    navigate(`/mycraftz/journey/${showcaseSlug}`);
                  }}
                  onNext={next} onBack={prev}
                />
              )}
              {/* STEP 6 — REFLECT */}
              {STEPS[stepIdx] === "reflect" && (
                <ReflectStep
                  key="reflect" copy={copy.reflect}
                  reflection={reflection} onChange={setReflection}
                  saved={reflectionSaved}
                  onSave={saveReflection}
                  onBackToLibrary={onClose}
                  onShare={sharePlan}
                  onBack={prev}
                />
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

/* ──────────────────────────────────────────────────────────────────
   STEP CARDS
   ────────────────────────────────────────────────────────────────── */

function StepShell({ children, testId }) {
  return (
    <motion.div
      className="mci-step"
      initial={{ opacity: 0, x: 18 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -18 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      data-testid={testId}
    >
      {children}
    </motion.div>
  );
}

function PrimaryBtn({ children, disabled, onClick, testId }) {
  return (
    <button
      type="button"
      className="mci-primary-btn"
      disabled={disabled}
      onClick={onClick}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

function SecondaryBtn({ children, onClick, testId }) {
  return (
    <button type="button" className="mci-secondary-btn" onClick={onClick} data-testid={testId}>
      {children}
    </button>
  );
}

// ── STEP 1 — RECALL ─────────────────────────────────────────────
function RecallStep({ book, snapshot, copy, onNext }) {
  const summary = clampSentences(snapshot.summary, 3);
  const insights = (snapshot.insights || []).filter(Boolean).slice(0, 5);
  const coreTheme = snapshot.coreTheme || "";
  return (
    <StepShell testId="mci-step-recall">
      <h2 className="mci-heading">{copy?.heading || "What This Book Taught You"}</h2>
      <p className="mci-eyebrow">{book.title} <em>— {book.author}</em></p>

      {summary && (
        <section className="mci-block">
          <h3 className="mci-block-label">{copy?.summaryLabel || "The Summary"}</h3>
          <p className="mci-block-body">{summary}</p>
        </section>
      )}

      {insights.length > 0 && (
        <section className="mci-block">
          <h3 className="mci-block-label">{copy?.insightsLabel || "Key Insights"}</h3>
          <ol className="mci-insights">
            {insights.map((it, i) => (
              <li key={`recall-insight-${i}`}><span className="mci-insight-num">{i + 1}</span>{it}</li>
            ))}
          </ol>
        </section>
      )}

      {coreTheme && (
        <section className="mci-block">
          <h3 className="mci-block-label">{copy?.coreThemeLabel || "Core Theme"}</h3>
          <p className="mci-block-body mci-core-theme"><strong>{coreTheme}</strong></p>
        </section>
      )}

      <div className="mci-actions">
        <PrimaryBtn onClick={onNext} testId="mci-recall-cta">
          {copy?.cta || "Choose What To Install →"}
        </PrimaryBtn>
      </div>
    </StepShell>
  );
}

// ── STEP 2 — CHOOSE ─────────────────────────────────────────────
function ChooseStep({ copy, ideas, loading, error, onRetry, selectedIdeaId, onSelect, onNext, onBack }) {
  return (
    <StepShell testId="mci-step-choose">
      <h2 className="mci-heading">{copy?.heading || "Pick One Idea To Install"}</h2>

      {loading && <p className="mci-loading">{copy?.loading || "Discovering the ten ideas worth installing…"}</p>}
      {error && (
        <div className="mci-error">
          <p>{error}</p>
          <SecondaryBtn onClick={onRetry} testId="mci-ideas-retry">{copy?.retry || "Try again"}</SecondaryBtn>
        </div>
      )}

      {!loading && !error && ideas.length > 0 && (
        <ul className="mci-idea-list" role="radiogroup">
          {ideas.map((idea) => (
            <li key={idea.id}>
              <button
                type="button"
                role="radio"
                aria-checked={selectedIdeaId === idea.id}
                className={`mci-idea-card ${selectedIdeaId === idea.id ? "is-active" : ""}`}
                onClick={() => onSelect(idea.id)}
                data-testid={`mci-idea-${idea.id}`}
              >
                <div className="mci-idea-radio" aria-hidden="true">
                  {selectedIdeaId === idea.id && <Check size={12} strokeWidth={3} />}
                </div>
                <div className="mci-idea-text">
                  <p className="mci-idea-title">{idea.title}</p>
                  <p className="mci-idea-desc">{idea.description}</p>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mci-actions">
        <SecondaryBtn onClick={onBack} testId="mci-choose-back"><ArrowLeft size={14} /> Back</SecondaryBtn>
        <PrimaryBtn disabled={!selectedIdeaId} onClick={onNext} testId="mci-choose-cta">
          {copy?.cta || "Commit To This →"}
        </PrimaryBtn>
      </div>
    </StepShell>
  );
}

// ── STEP 3 — COMMIT ─────────────────────────────────────────────
function CommitStep({ copy, selectedIdea, why, loading, committed, onCommitChange, onNext, onBack }) {
  return (
    <StepShell testId="mci-step-commit">
      <h2 className="mci-heading">{copy?.heading || "You Have Chosen"}</h2>
      <p className="mci-idea-headline">{selectedIdea?.title}</p>
      {loading ? (
        <p className="mci-loading">{copy?.whyLoading || "Reflecting on why this matters…"}</p>
      ) : (
        why && <blockquote className="mci-why">{why}</blockquote>
      )}

      <label className="mci-commit-row">
        <input
          type="checkbox"
          checked={committed}
          onChange={(e) => onCommitChange(e.target.checked)}
          data-testid="mci-commit-checkbox"
        />
        <span>{copy?.checkboxLabel || "I commit to practising this for 30 days."}</span>
      </label>

      <div className="mci-actions">
        <SecondaryBtn onClick={onBack} testId="mci-commit-back"><ArrowLeft size={14} /> Back</SecondaryBtn>
        <PrimaryBtn disabled={!committed} onClick={onNext} testId="mci-commit-cta">
          {copy?.cta || "Build My Plan →"}
        </PrimaryBtn>
      </div>
    </StepShell>
  );
}

// ── STEP 4 — PLAN ───────────────────────────────────────────────
function PlanStep({ copy, plan, loadingPlan, quote, loadingQuote, onNext, onBack }) {
  return (
    <StepShell testId="mci-step-plan">
      <h2 className="mci-heading">{copy?.heading || "Your 30-Day Blueprint"}</h2>

      {loadingPlan && <p className="mci-loading">{copy?.loading || "Designing your daily practice…"}</p>}

      {plan && (
        <div className="mci-plan-grid">
          <div className="mci-plan-row"><span className="mci-plan-label">⚡ {copy?.triggerLabel || "Trigger"}</span><span className="mci-plan-value">{plan.trigger}</span></div>
          <div className="mci-plan-row"><span className="mci-plan-label">🎯 {copy?.actionLabel || "Daily Action"}</span><span className="mci-plan-value">{plan.action}</span></div>
          <div className="mci-plan-row"><span className="mci-plan-label">⏱ {copy?.durationLabel || "Duration"}</span><span className="mci-plan-value">{plan.duration}</span></div>
          <div className="mci-plan-row"><span className="mci-plan-label">🔔 {copy?.reminderLabel || "Reminder"}</span><span className="mci-plan-value">{plan.reminder}</span></div>
        </div>
      )}

      {loadingQuote && <p className="mci-loading">{copy?.quoteLoading || "Finding a verse for this idea…"}</p>}

      {quote && (
        <blockquote className="mci-quote">
          <p className="mci-quote-sanskrit indic">{quote.sanskrit}</p>
          <p className="mci-quote-translation">{quote.translation}</p>
          <p className="mci-quote-source">— {quote.source}</p>
        </blockquote>
      )}

      <div className="mci-actions">
        <SecondaryBtn onClick={onBack} testId="mci-plan-back"><ArrowLeft size={14} /> Back</SecondaryBtn>
        <PrimaryBtn
          onClick={onNext}
          testId="mci-plan-cta"
          disabled={loadingPlan || loadingQuote || !plan}
        >
          {copy?.cta || "Start My 30 Days →"}
        </PrimaryBtn>
      </div>
    </StepShell>
  );
}

// ── STEP 5 — ACTION ─────────────────────────────────────────────
function ActionStep({ copy, plan, done, onDone, showContinueCta, onContinueToEngine, onNext, onBack }) {
  return (
    <StepShell testId="mci-step-action">
      <h2 className="mci-heading">{copy?.heading || "Today's Practice"}</h2>
      <p className="mci-day-line">{copy?.dayLine || "Day 1 of 30"}</p>

      {plan && (
        <>
          <p className="mci-action-line">{plan.action}</p>
          <p className="mci-action-duration">
            <em>{plan.duration}{copy?.durationSuffix || ". That is enough."}</em>
          </p>
        </>
      )}

      <div className="mci-action-cta-wrap">
        {!done ? (
          <PrimaryBtn onClick={onDone} testId="mci-action-done">
            {copy?.doneCta || "I've Done It ✓"}
          </PrimaryBtn>
        ) : (
          <motion.div
            className="mci-done-toast"
            initial={{ scale: 0.94, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.4 }}
            data-testid="mci-done-toast"
          >
            <Check size={28} strokeWidth={2.4} /> {copy?.doneToast || "Well done. Come back tomorrow."}
          </motion.div>
        )}
      </div>

      {done && <p className="mci-action-sub">{copy?.sub || "Come back tomorrow for Day 2."}</p>}

      <div className="mci-actions">
        <SecondaryBtn onClick={onBack} testId="mci-action-back"><ArrowLeft size={14} /> Back</SecondaryBtn>
        {showContinueCta && done && (
          <PrimaryBtn onClick={onContinueToEngine} testId="mci-action-continue">
            {copy?.continueCta || "Continue in MyCraftz™ →"}
          </PrimaryBtn>
        )}
        {!showContinueCta && done && (
          <PrimaryBtn onClick={onNext} testId="mci-action-next">Reflect →</PrimaryBtn>
        )}
      </div>
    </StepShell>
  );
}

// ── STEP 6 — REFLECT ────────────────────────────────────────────
function ReflectStep({ copy, reflection, onChange, saved, onSave, onBackToLibrary, onShare, onBack }) {
  if (saved) {
    return (
      <StepShell testId="mci-step-reflect-saved">
        <h2 className="mci-heading">{copy?.savedTitle || "Your reflection has been saved."}</h2>
        <div className="mci-actions mci-actions--center">
          <SecondaryBtn onClick={onBackToLibrary} testId="mci-reflect-library">
            {copy?.backToLibrary || "Back to Library"}
          </SecondaryBtn>
          <SecondaryBtn onClick={onShare} testId="mci-reflect-share">
            <Share2 size={14} /> {copy?.share || "Share My Commitment"}
          </SecondaryBtn>
        </div>
      </StepShell>
    );
  }

  const handleField = (key) => (v) => onChange({ ...reflection, [key]: v });
  return (
    <StepShell testId="mci-step-reflect">
      <h2 className="mci-heading">{copy?.heading || "Capture Today's Learning"}</h2>
      <VoiceField
        label={copy?.f1Label || "What did I notice today?"}
        placeholder={copy?.f1Placeholder || "Share what came up..."}
        value={reflection.noticed} onChange={handleField("noticed")}
        testId="mci-reflect-noticed"
      />
      <VoiceField
        label={copy?.f2Label || "What was difficult?"}
        placeholder={copy?.f2Placeholder || "Be honest..."}
        value={reflection.difficult} onChange={handleField("difficult")}
        testId="mci-reflect-difficult"
      />
      <VoiceField
        label={copy?.f3Label || "What will I do tomorrow?"}
        placeholder={copy?.f3Placeholder || "Your intention..."}
        value={reflection.tomorrow} onChange={handleField("tomorrow")}
        testId="mci-reflect-tomorrow"
      />

      <div className="mci-actions">
        <SecondaryBtn onClick={onBack} testId="mci-reflect-back"><ArrowLeft size={14} /> Back</SecondaryBtn>
        <PrimaryBtn onClick={onSave} testId="mci-reflect-save">{copy?.saveCta || "Save Reflection"}</PrimaryBtn>
      </div>
    </StepShell>
  );
}

// ── Reusable text field with Web Speech API mic ─────────────────
function VoiceField({ label, placeholder, value, onChange, testId }) {
  const [listening, setListening] = useState(false);
  const recogRef = useRef(null);

  const supportsSpeech =
    typeof window !== "undefined" &&
    (window.SpeechRecognition || window.webkitSpeechRecognition);

  const toggleMic = () => {
    if (!supportsSpeech) return;
    if (listening) {
      try { recogRef.current?.stop(); } catch { /* ignored */ }
      setListening(false);
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = "en-IN";
    rec.onresult = (event) => {
      let final = "";
      for (let i = event.resultIndex; i < event.results.length; ++i) {
        if (event.results[i].isFinal) final += event.results[i][0].transcript;
      }
      if (final) onChange((value || "") + (value ? " " : "") + final.trim());
    };
    rec.onerror = (err) => { setListening(false); };
    rec.onend = () => setListening(false);
    recogRef.current = rec;
    try { rec.start(); setListening(true); } catch { /* ignored */ }
  };

  return (
    <div className="mci-field" data-testid={testId}>
      <label className="mci-field-label">{label}</label>
      <div className="mci-field-row">
        <textarea
          rows={2}
          className="mci-field-input"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`${testId}-input`}
        />
        {supportsSpeech && (
          <button
            type="button"
            className={`mci-mic-btn ${listening ? "is-on" : ""}`}
            onClick={toggleMic}
            aria-label={listening ? "Stop voice input" : "Start voice input"}
            data-testid={`${testId}-mic`}
          >
            {listening ? <MicOff size={16} /> : <Mic size={16} />}
          </button>
        )}
      </div>
    </div>
  );
}
